import { Component, OnInit, ViewChild } from '@angular/core';
import {
  AbstractControl,
  FormGroup,
  UntypedFormBuilder,
  UntypedFormControl,
  UntypedFormGroup,
  ValidationErrors,
  Validators,
  FormsModule,
  ReactiveFormsModule,
} from '@angular/forms';
import {
  MatTableDataSource,
  MatTable,
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
  MatFooterCellDef,
  MatFooterCell,
  MatHeaderRowDef,
  MatHeaderRow,
  MatRowDef,
  MatRow,
  MatFooterRowDef,
  MatFooterRow,
} from '@angular/material/table';
import { ActivatedRoute, Router } from '@angular/router';
import { userMixin } from '@app/users/mixins/user-mixin';
import { ButtonSize, ButtonStyle } from '@constants/button/1.0';
import { LoadingColor, LoadingType } from '@constants/loading/1.0';
import { MessageType } from '@constants/message/1.0';
import { FormCanDeactivate } from '@extensions/form-can-deactivate';
import { forkJoin, Observable, of, Subject, Subscription, throwError, filter } from 'rxjs';
import { first, takeUntil } from 'rxjs/operators';
import {
  Bonus,
  Coupon,
  DistributionJobRequest,
  DistributionRule,
  DistributionSchedule,
  Job,
  Reward,
  Segment,
  User,
} from '@generated/tcea/models';
import { DistributionRulesService } from '@services/distribution-rules/1.0/distribution-rules.service';
import { PermissionsService } from '@services/permissions/1.0/permissions.service';
import { SegmentsService } from '@services/segments/1.0/segments.service';
import { Sort, MatSort, MatSortHeader } from '@angular/material/sort';
import { AuthService } from '@auth0/auth0-angular';
import { TranslateService, TranslateModule } from '@ngx-translate/core';
import { CouponService } from '@services/coupon/1.0/coupon.service';
import { MatSelect } from '@angular/material/select';
import {
  NGX_MAT_DATE_FORMATS,
  NgxMatDatetimepicker,
  NgxMatDatetimePickerModule,
} from '@nng-components/angular-material-datetime-picker';
import { MatDialog } from '@angular/material/dialog';
import { ThemePalette, MatOption } from '@angular/material/core';
import { ConfirmationComponent, ConfirmationDialogData } from '@components/confirmation/1.0';
import { LoyaltyService } from '../../services/implementations/loyalty/1.0/loyalty.service';
import { TceLocaleService } from '@services/tce-locale/1.0/tce-locale.service';
import { DateService } from '@services/utilities/1.0/date.service';
import { UtilitiesService } from '../../services/implementations/utilities/1.0/utilities.service';
import { TimeZone } from '@interfaces/timeZone/1.0';
import { DistributionJobDialogComponent } from '@components/distributon-job-dialog/distribution-job-dialog.component';
import { CUSTOM_DATE_FORMATS } from '@constants/datetime/1.0';
import { NgIf, NgFor, NgClass, DatePipe } from '@angular/common';
import { LoadingComponent } from '../loading/1.0/loading.component';
import { MessageComponent } from '../message/1.0/message.component';
import { MatFormField, MatLabel, MatError, MatSuffix } from '@angular/material/form-field';
import { MatInput } from '@angular/material/input';
import { MatSlideToggle } from '@angular/material/slide-toggle';
import { TxpIntegersOnlyDirective } from '../../directives/integers-only/1.0/txp-integers-only.directive';
import { ButtonComponent } from '../button/1.0/button.component';
import { MatDatepickerToggle } from '@angular/material/datepicker';
import { MatIconButton } from '@angular/material/button';
@Component({
  selector: 'txp-edit-rule',
  templateUrl: './edit-rule.component.html',
  styleUrls: ['./edit-rule.component.scss'],
  providers: [{ provide: NGX_MAT_DATE_FORMATS, useValue: CUSTOM_DATE_FORMATS }],
  standalone: true,
  imports: [
    NgIf,
    LoadingComponent,
    MessageComponent,
    FormsModule,
    ReactiveFormsModule,
    MatFormField,
    MatLabel,
    MatInput,
    MatError,
    MatSlideToggle,
    MatSelect,
    NgFor,
    MatOption,
    NgClass,
    TxpIntegersOnlyDirective,
    ButtonComponent,
    NgxMatDatetimePickerModule,
    MatDatepickerToggle,
    MatSuffix,
    MatIconButton,
    MatTable,
    MatSort,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatSortHeader,
    MatCellDef,
    MatCell,
    MatFooterCellDef,
    MatFooterCell,
    MatHeaderRowDef,
    MatHeaderRow,
    MatRowDef,
    MatRow,
    MatFooterRowDef,
    MatFooterRow,
    TranslateModule,
    DatePipe,
  ],
})
export class EditRuleComponent extends userMixin(FormCanDeactivate) implements OnInit {
  @ViewChild('selectInput') selectInput: MatSelect;
  @ViewChild('jobStartDatePicker') jobStartDatePicker: NgxMatDatetimepicker<any>;
  @ViewChild('recurringStartDatePicker') recurringStartDatePicker: NgxMatDatetimepicker<any>;
  @ViewChild('recurringEndDatePicker') recurringEndDatePicker: NgxMatDatetimepicker<any>;

  form: UntypedFormGroup;
  jobToAdd: UntypedFormGroup;
  buttonStyle = ButtonStyle; // expose enum to template
  buttonSize = ButtonSize; // expose enum to template
  messageType = MessageType; // expose enum to template
  loadingColor = LoadingColor; // expose enum to template
  loadingType = LoadingType; // expose enum to template
  addingJob = false;
  editingJob: Job = null;
  displayColumns = ['startTime', 'finishedTime', 'audienceIds', 'status', 'action'];
  itemSearch = false;
  cancellingJob: string;
  segmentSearch = false;
  isLoading = false;
  jobsLoading = false;
  lastSavedDistributionRule: DistributionRule;
  showLoadError = false;
  showItemsLoadError = false;
  showCancelJobError = false;
  showJobLoadingError = false;
  showSegmentsLoadError = false;
  showSaveError = false;
  showSaveJobsError = false;
  sort = { direction: '', field: '' };
  isSaving = false;
  rulesReadOnly = true;
  itemsList: MatTableDataSource<Coupon | Reward | Bonus> = new MatTableDataSource<
    Coupon | Reward | Bonus
  >([]);
  jobsData: MatTableDataSource<DistributionJobRequest> =
    new MatTableDataSource<DistributionJobRequest>();
  segmentsList: MatTableDataSource<Segment> = new MatTableDataSource<Segment>();
  public color: ThemePalette = 'primary';
  selectedItems: Coupon[] | Reward[] | Bonus[]; // selected items
  selectedSegments: Segment[];
  selectedRuleType: DistributionRule['type'];
  selectedFrequencyType: DistributionSchedule['frequency'];
  ruleTypes;
  timeZones: TimeZone[];
  filteredTimeZones: TimeZone[];
  frequencies;
  frequencyModifiers;
  minDate = null;
  newDistributionRule = true;
  createDistributionRuleSuccess = false;
  formSubmitted = false;
  brandId: string;
  rule: DistributionRule;
  user: User;
  WEEK_MAP = {
    1: 'MONDAY',
    2: 'TUESDAY',
    3: 'WEDNESDAY',
    4: 'THURSDAY',
    5: 'FRIDAY',
    6: 'SATURDAY',
    7: 'SUNDAY',
  };

  subscription: Subscription;

  private unsubscribe = new Subject<void>();

  constructor(
    private authService: AuthService,
    private dialog: MatDialog,
    private couponService: CouponService,
    private loyaltyService: LoyaltyService,
    private formBuilder: UntypedFormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private permissionsService: PermissionsService,
    private distributionRulesService: DistributionRulesService,
    private segmentsService: SegmentsService,
    private translateService: TranslateService,
    private tceLocaleService: TceLocaleService,
    private dateService: DateService,
    private utilitiesService: UtilitiesService,
  ) {
    super();
    this.loyaltyItemTypes = [
      {
        value: 'LOYALTY_REWARDS',
        label: this.translateService.instant('coupons.rules.grid.fields.rewards'),
      },
      {
        value: 'LOYALTY_POINTS',
        label: this.translateService.instant('coupons.rules.grid.fields.bonus'),
      },
    ];
    this.ruleTypes = [
      {
        value: 'ONE_TIME',
        label: this.translateService.instant('coupons.rules.grid.fields.type.oneTime'),
      },
      {
        value: 'RECURRING',
        label: this.translateService.instant('coupons.rules.grid.fields.type.recurring'),
      },
      {
        value: 'EVENT',
        label: this.translateService.instant('coupons.rules.grid.fields.type.event'),
      },
    ];

    this.frequencies = [
      {
        value: 'DAILY',
        label: this.translateService.instant('coupons.rules.grid.edit.schedule.frequency.day'),
      },
      {
        value: 'MONTHLY',
        label: this.translateService.instant('coupons.rules.grid.edit.schedule.frequency.month'),
      },
      {
        value: 'WEEKLY',
        label: this.translateService.instant('coupons.rules.grid.edit.schedule.frequency.week'),
      },
      {
        value: 'YEARLY',
        label: this.translateService.instant('coupons.rules.grid.edit.schedule.frequency.year'),
      },
    ];
    this.rulesReadOnly =
      this.permissionsService.getPermission('coupon-management:read') &&
      !this.permissionsService.getPermission('coupon-management:write');
    this.form = this.formBuilder.group({
      archived: new UntypedFormControl(false),
      items: new UntypedFormControl([], Validators.required),
      enabled: new UntypedFormControl(true),
      filterItems: new UntypedFormControl(),
      filterSegments: new UntypedFormControl(),
      maxDistributionsPerUser: new UntypedFormControl(''),
      name: new UntypedFormControl('', Validators.required),
      segments: new UntypedFormControl([]),
      type: new UntypedFormControl(this.ruleTypes[0].value, this.typeValidator.bind(this)),
      newJobs: new UntypedFormControl([]),
      schedule: this.formBuilder.group({
        timeZone: new UntypedFormControl(''),
        startDate: new UntypedFormControl({ value: '', disabled: true }),
        endDate: new UntypedFormControl({ value: '', disabled: true }),
        frequency: new UntypedFormControl(this.frequencies[0].value),
        frequencyModifier: new UntypedFormControl(1),
        dayOfMonth: new UntypedFormControl(''),
        timeOfDay: new UntypedFormControl(''),
      }),
    });

    this.loyaltyRule = this.router?.url?.includes('loyalty');
    if (this.loyaltyRule) {
      this.form.addControl('loyaltyItemType', new UntypedFormControl('', Validators.required));
    }

    this.route?.params
      .pipe(takeUntil(this.unsubscribe))
      .subscribe(this.handleRouteParams.bind(this));
  }

  handleRouteParams(params) {
    const brandId = window.sessionStorage.getItem('brand_id');
    this.newDistributionRule = true;
    this.rulesReadOnly ? this.form.disable() : this.form.enable();

    if (params?.id) {
      this.newDistributionRule = false;
      const state = this.router.getCurrentNavigation()?.extras?.state;
      this.isLoading = true;

      const getLoyaltyItems = () => {
        return this.rule.channelIds?.map((item) => {
          const values = item.split('|');
          return values[1]?.trim()?.replace(/\s/g, '');
        });
      };

      if (state?.data) {
        this.rule = state?.data;
        this.form.get('loyaltyItemType')?.setValue(this.rule.channel);
        let items = this.loyaltyRule ? getLoyaltyItems() : this.rule.channelIds;

        this.form.patchValue({
          ...this.rule,
          segments: this.rule.audienceIds,
          items,
        });

        this.lastSavedDistributionRule = Object.assign({}, this.form.value);
        this.form.updateValueAndValidity();
        this.selectedFrequencyType =
          this.form.value.schedule?.frequency || this.frequencies[0].value;
      } else {
        this.distributionRulesService.getRuleById(brandId, params?.id).subscribe({
          next: (rule: DistributionRule) => {
            this.rule = Object.assign({}, rule);
            let items = this.loyaltyRule ? getLoyaltyItems() : this.rule.channelIds;
            this.form.patchValue({
              ...this.rule,
              segments: this.rule.audienceIds,
              items,
            });

            if (this.loyaltyRule) {
              this.form.get('loyaltyItemType')?.setValue(this.rule.channel);
              this.onLoyaltyItemTypeChange(this.form.get('loyaltyItemType')?.value);
            }

            this.form.updateValueAndValidity();

            this.selectedFrequencyType =
              this.form.value.schedule?.frequency || this.frequencies[0].value;
            this.lastSavedDistributionRule = Object.assign({}, this.form.value);

            if (!this.segmentSearch) {
              this.isLoading = false;
            }
          },
          error: () => {
            this.showLoadError = true;
            this.isLoading = false;
          },
        });
      }

      this.getJobList(params.id);
    } else {
      this.lastSavedDistributionRule = Object.assign({}, this.form.value);
    }

    this.form.get('archived').disable();
  }

  ngOnInit(): void {
    this.loadLocaleData();
    const itemsControl = this.form.get('filterItems');
    itemsControl?.valueChanges?.subscribe(this.filterItems.bind(this));
    const segmentsControl = this.form.get('filterSegments');
    segmentsControl.valueChanges.subscribe(this.filterSegments.bind(this));
    const enabledControl = this.form.get('enabled');
    const handleEnabledValue = (newEnabled) => {
      const archivedControl = this.form.get('archived');

      if (!newEnabled && !this.newDistributionRule) {
        this.form.get('archived').enable();
      } else {
        archivedControl.setValue(false);
        archivedControl.disable();
      }
    };

    enabledControl.valueChanges.subscribe(handleEnabledValue);
    handleEnabledValue(enabledControl.value);

    const scheduleControl = this.form.get('schedule');
    const timeZoneControl = this.form.get('schedule.timeZone');
    const startDateControl = this.form.get('schedule.startDate');
    const endDateControl = this.form.get('schedule.endDate');

    const handleTimeZoneChange = (newTimeZone) => {
      if (newTimeZone) {
        if (startDateControl.disabled) {
          startDateControl.enable();
        }
      } else if (!startDateControl.disabled) {
        startDateControl.disable();
      }

      const now = new Date();
      const nowInTz = this.dateService.convertToTimeZone(now, newTimeZone);
      this.minDate = this.dateService.format(
        this.dateService.startOf(nowInTz, 'day'),
        'YYYY-MM-DD',
      );
    };

    timeZoneControl.valueChanges.subscribe(handleTimeZoneChange);
    handleTimeZoneChange(timeZoneControl.value);

    const handleStartDateChange = (newStartDate) => {
      if (newStartDate) {
        if (endDateControl.disabled) {
          endDateControl.enable();
        }
      } else if (!endDateControl.disabled) {
        endDateControl.disable();
      }
    };

    startDateControl.valueChanges.subscribe((newStartDate) => {
      handleStartDateChange(newStartDate);

      setTimeout(() => {
        endDateControl.updateValueAndValidity({ emitEvent: false });

        if (this.isEndDateOnOrBeforeStart()) {
          startDateControl.markAsTouched();
          startDateControl.updateValueAndValidity({ emitEvent: false });
        }
      });
    });
    handleStartDateChange(startDateControl.value);

    endDateControl.valueChanges.subscribe(() => {
      setTimeout(() => {
        startDateControl.updateValueAndValidity({ emitEvent: false });

        if (this.isEndDateOnOrBeforeStart()) {
          endDateControl.markAsTouched();
          endDateControl.updateValueAndValidity({ emitEvent: false });
        }
      });
    });

    const frequencyControl = this.form.get('schedule.frequency');
    frequencyControl.valueChanges.subscribe(this.createFrequencyModifiers.bind(this));
    this.createFrequencyModifiers(frequencyControl.value);

    scheduleControl.valueChanges.subscribe((scheduleValue) => {
      if (!this.scheduleIsCompleted(scheduleValue)) {
        return;
      }

      setTimeout(() => {
        this.validateAllFormFields(scheduleControl as FormGroup);
      }, 0);
    });

    const now = this.dateService.convertToTimeZone(new Date(), timeZoneControl.value);
    this.minDate = this.dateService.format(this.dateService.startOf(now, 'day'), 'YYYY-MM-DD');

    this.authService.user$.subscribe((user) => {
      this.user = user;
    });

    if (this.loyaltyRule) {
      this.programId = this.router.url?.split('/')[3];
      this.getLoyaltyItems();
    } else {
      this.getCouponList();
    }

    this.getSegmentList();
  }

  loadLocaleData() {
    this.tceLocaleService
      .getLocaleData()
      .pipe(takeUntil(this.unsubscribe))
      .subscribe({
        next: (localeData) => {
          this.timeZones = localeData.timeZones;
          this.filteredTimeZones = [...localeData.timeZones];
        },
        error: () => {
          this.showDataLoadError = true;
        },
      });
  }

  toDateKey(date): string {
    if (!date) {
      return '';
    }

    if (date instanceof Date) {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    }

    const value = String(date).split('T')[0];
    const usDate = value.match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
    if (usDate) {
      const [, month, day, year] = usDate;
      return `${year}-${month.padStart(2, '0')}-${day.padStart(2, '0')}`;
    }

    return value;
  }

  isEndDateOnOrBeforeStart(): boolean {
    const schedule = this.form?.get('schedule')?.value;
    const startKey = this.toDateKey(
      this.form?.get('schedule.startDate')?.value ?? schedule?.startDate,
    );
    const endKey = this.toDateKey(this.form?.get('schedule.endDate')?.value ?? schedule?.endDate);
    return !!startKey && !!endKey && endKey <= startKey;
  }

  startDateFilter = (date: Date | null): boolean => {
    if (!date) {
      return false;
    }

    const day = this.toDateKey(date);
    if (this.minDate && day >= this.minDate) {
      return true;
    }

    if (this.newDistributionRule) {
      return false;
    }

    const savedStart = this.toDateKey(this.lastSavedDistributionRule?.schedule?.startDate);
    return !!savedStart && day === savedStart;
  };

  scheduleIsCompleted(providedValue) {
    const scheduleValue = providedValue || this.form.get('schedule').value;
    const basicFieldsMissing =
      !scheduleValue.startDate || !scheduleValue.endDate || !scheduleValue.frequency;
    const dayOfMonthMissing = scheduleValue.frequency === 'MONTHLY' && !scheduleValue.dayOfMonth;
    const specificDayMissing =
      scheduleValue.frequency !== 'MONTHLY' && !scheduleValue.frequencyModifier;
    const timeMissing = !scheduleValue.timeOfDay;

    return !basicFieldsMissing && !dayOfMonthMissing && !specificDayMissing && !timeMissing;
  }

  scheduleDateValidator(formControl: AbstractControl): ValidationErrors | undefined {
    if (!this.form) {
      return undefined;
    }

    const schedule = this.form.get('schedule').value;
    const startDateControl = this.form.get('schedule.startDate');
    const endDateControl = this.form.get('schedule.endDate');

    if (this.isEndDateOnOrBeforeStart()) {
      if (formControl === endDateControl) {
        return { matDatetimePickerMin: true };
      }

      if (formControl === startDateControl) {
        return { startAfterEnd: true };
      }
    }

    if (!this.scheduleIsCompleted(schedule)) {
      if (!this.formSubmitted && !this.isEndDateOnOrBeforeStart()) {
        setTimeout(() => formControl.markAsUntouched(), 0);
      }
      return undefined;
    }

    const savedSchedule = this.lastSavedDistributionRule?.schedule;
    const datesUnchangedOnEdit =
      !this.newDistributionRule &&
      !!savedSchedule &&
      this.toDateKey(schedule.startDate) === this.toDateKey(savedSchedule.startDate) &&
      this.toDateKey(schedule.endDate) === this.toDateKey(savedSchedule.endDate);

    const startDateValue =
      typeof schedule.startDate === 'object' && schedule.startDate
        ? this.dateService.format(schedule.startDate, 'YYYY-MM-DD')
        : schedule.startDate;

    const startDate = new Date(startDateValue);
    const startDateInTz = this.dateService.convertToTimeZone(startDate, schedule.timeZone);

    const [hours, minutes] = (schedule.timeOfDay || '').split(':');
    const startDateAndTime = new Date(startDateInTz);
    startDateAndTime.setHours(parseInt(hours || '0', 10), parseInt(minutes || '0', 10));

    const endDateValue =
      typeof schedule.endDate === 'object' && schedule.endDate
        ? this.dateService.format(schedule.endDate, 'YYYY-MM-DD')
        : schedule.endDate;

    const endDate = new Date(endDateValue);
    const endDateInTz = new Date(endDate.toLocaleString('en-US', { timeZone: schedule.timeZone }));
    endDateInTz.setHours(23, 59, 59, 999);

    const now = new Date();
    const nowInTz = this.dateService.convertToTimeZone(now, schedule.timeZone);
    const fifteenMinutesAfter = this.dateService.add(nowInTz, 15, 'minute');

    const isValidOccurrence = (occurrence: Date) => {
      const inWindow =
        occurrence.getTime() >= startDateAndTime.getTime() &&
        occurrence.getTime() <= endDateInTz.getTime();

      if (!inWindow) {
        return false;
      }

      return datesUnchangedOnEdit || occurrence.getTime() >= fifteenMinutesAfter.getTime();
    };

    const START_OF_MAP = {
      DAILY: 'day',
      MONTHLY: 'month',
      WEEKLY: 'week',
    };

    let result;

    if (schedule.frequency === 'YEARLY') {
      const intervalYears = schedule.frequencyModifier || 1;

      if (isValidOccurrence(startDateAndTime)) {
        result = undefined;
      } else {
        const nextOccurrence = this.dateService.add(startDateAndTime, intervalYears, 'year');
        result = isValidOccurrence(nextOccurrence) ? undefined : { invalid: true };
      }
    } else if (START_OF_MAP[schedule.frequency]) {
      const startOfPeriod = this.dateService.startOf(
        startDateInTz,
        START_OF_MAP[schedule.frequency],
      );
      let amountToAdd = (schedule.frequencyModifier || 1) - 1;

      if (schedule.frequency === 'MONTHLY') {
        amountToAdd = (schedule.dayOfMonth || 1) - 1;
      }

      const startDateAtSpecifiedTime = this.dateService.add(startOfPeriod, amountToAdd, 'day');
      startDateAtSpecifiedTime.setHours(parseInt(hours || '0', 10), parseInt(minutes || '0', 10));

      if (isValidOccurrence(startDateAtSpecifiedTime)) {
        result = undefined;
      } else {
        const firstRun = this.dateService.add(
          this.dateService.add(startOfPeriod, 1, START_OF_MAP[schedule.frequency]),
          amountToAdd,
          'day',
        );
        firstRun.setHours(parseInt(hours || '0', 10), parseInt(minutes || '0', 10));

        result = isValidOccurrence(firstRun) ? undefined : { invalid: true };
      }
    }

    return result;
  }

  typeValidator(formControl: AbstractControl): ValidationErrors | undefined {
    if (!this.form) {
      return undefined;
    }

    const newJobsControl = this.form.get('newJobs');

    newJobsControl.setValue([]);

    const segmentsControl = this.form.get('segments');
    const scheduleTimeOfDay = this.form.get('schedule.timeOfDay');
    const scheduleStartDateControl = this.form.get('schedule.startDate');
    const scheduleEndDateControl = this.form.get('schedule.endDate');

    if (formControl.value !== this.form.value.type) {
      this.jobsData.data = this.jobsData.data.filter((job) => !newJobsControl.value.includes(job));
      segmentsControl.setValue([]);
    }

    const controls = [
      segmentsControl,
      scheduleTimeOfDay,
      scheduleStartDateControl,
      scheduleEndDateControl,
    ];

    controls.forEach((control) => {
      control.clearValidators();
      control.updateValueAndValidity({ onlySelf: true });
    });

    if (segmentsControl && (formControl.value === 'EVENT' || formControl.value === 'RECURRING')) {
      segmentsControl.setValidators([Validators.required]);
    }

    if (formControl.value === 'RECURRING') {
      scheduleTimeOfDay.setValidators([Validators.required, this.scheduleDateValidator.bind(this)]);
      scheduleStartDateControl.setValidators([
        Validators.required,
        this.scheduleDateValidator.bind(this),
      ]);
      scheduleEndDateControl.setValidators([
        Validators.required,
        this.scheduleDateValidator.bind(this),
      ]);
    }

    CUSTOM_DATE_FORMATS.display.dateInput =
      formControl.value === 'RECURRING' ? 'M/d/yyyy' : 'M/d/yyyy HH:mm:ss a';
    CUSTOM_DATE_FORMATS.parse.dateInput =
      formControl.value === 'RECURRING' ? 'M/d/yyyy' : 'M/d/yyyy HH:mm:ss a';

    return this.newDistributionRule ? Validators.required(formControl) : undefined;
  }

  getSegmentList() {
    this.segmentSearch = true;
    this.segmentsList.data = [];
    this.segmentsService.getAllSegments(this.brandId).subscribe({
      next: (segments: Segment[]) => {
        if (segments) {
          this.segmentsList.data = segments;
        }
      },
      error: (err) => {
        throwError(err);
        this.segmentSearch = false;
        this.showSegmentsLoadError = true;

        if (this.rule) {
          this.isLoading = false;
        }
      },
      complete: () => {
        this.segmentSearch = false;

        if (this.rule) {
          this.isLoading = false;
        }
      },
    });
  }

  filterSegments(filter: any = ''): void {
    this.segmentsList.filter = filter?.trim().toLowerCase();
  }

  createFrequencyModifiers(period): void {
    this.frequencyModifiers = [];

    if (period === 'DAILY' || period === 'MONTHLY') {
      this.frequencyModifiers = new Array(31)
        .fill(0)
        .map((_, i) => ({ label: i + 1, value: i + 1 }));
    }

    if (period === 'WEEKLY') {
      this.frequencyModifiers = new Array(7)
        .fill(0)
        .map((_, i) => ({ label: this.WEEK_MAP[i + 1], value: i + 1 }));
    }

    if (period === 'YEARLY') {
      this.frequencyModifiers = new Array(10)
        .fill(0)
        .map((_, i) => ({ label: i + 1, value: i + 1 }));
    }
  }

  getCouponList(): void {
    this.selectedItems = [];
    this.itemsSearch = true;
    this.itemsList.data = [];
    this.couponService
      .get(this.brandId, { query: { enabled: true, archived: false }, sort: this.sort })
      .subscribe({
        next: ({ data, pages }) => {
          this.itemsList.data = data;
          this.itemsSearch = false;
        },
        error: (err) => {
          throwError(err);
          this.itemsSearch = false;
          this.showCouponsLoadError = true;
        },
      });
  }

  getLoyaltyItems() {
    this.isLoading = true;

    let rewards = this.loyaltyService.getAllRewards(this.brandId, this.programId);
    let bonuses = this.loyaltyService.getAllBonuses(this.brandId, this.programId);

    forkJoin({ rewards, bonuses }).subscribe({
      next: (data: { rewards: Reward[]; bonuses: Bonus[] }) => {
        this.itemsList.data = [];
        this.rewards = data.rewards;
        this.bonuses = data.bonuses;
        this.onLoyaltyItemTypeChange(this.form.get('loyaltyItemType').value);
        this.isLoading = false;
      },
      error: () => {
        this.loadingError = true;
        this.isLoading = false;
      },
    });
  }

  filterItems(filter: any = ''): void {
    this.itemsList.filter = filter?.trim().toLowerCase();
  }

  onLoyaltyItemTypeChange(event) {
    this.itemsList.data =
      event === 'LOYALTY_REWARDS' ? this.rewards : event === 'LOYALTY_POINTS' ? this.bonuses : [];
    let items = this.form.get('items').value;

    if (items.length) {
      items = items.filter((item) => this.itemsList.data.find((i) => i.id === item));
    }

    this.form.get('items').setValue(items);
  }

  getJobList(ruleId) {
    if (this.form.get('type').value === 'EVENT') {
      return;
    }

    this.jobsData.data = [];
    this.jobsLoading = true;

    this.distributionRulesService.getJobsForRule(this.brandId, ruleId).subscribe({
      next: (data) => {
        this.jobsData.data = data;
        this.jobsLoading = false;
      },
      error: (err) => {
        throwError(err);
        this.jobsLoading = false;
        this.showJobLoadingError = true;
      },
    });
  }

  setSorting({ active, direction }: Sort) {
    this.sort = { direction, field: active };
  }

  onCancel() {
    const newJobs = this.form.get('newJobs').value;

    this.addingJob = false;
    this.editingJob = undefined;
    this.jobsData.data = this.jobsData.data.filter((job) => job.id || !newJobs.includes(job));
    this.formSubmitted = false;
    this.form.reset(this.lastSavedDistributionRule, { emitEvent: false });
    this.form.markAsPristine();
    this.form.markAsUntouched();

    this.selectedFrequencyType = this.form.value.schedule?.frequency || this.frequencies[0].value;

    const periodControl = this.form.get('schedule.frequency');
    this.createFrequencyModifiers(periodControl.value);
  }

  checkForArchiveWarning(saveData) {
    if (saveData.archived === false || this.lastSavedDistributionRule.archived === true) {
      return of(true);
    }

    const modalContent: ConfirmationDialogData = {
      title: this.translateService.instant('coupons.rules.grid.edit.archiveTitle'),
      message: this.translateService.instant('coupons.rules.grid.edit.archiveMsg', {
        name: saveData.name,
      }),
      cancelText: this.translateService.instant('form.button.cancel'),
      confirmText: this.translateService.instant('form.button.continue'),
    };

    const dialogRef = this.dialog.open(ConfirmationComponent, {
      data: modalContent,
    });

    return new Observable((observer) =>
      dialogRef.afterClosed().subscribe((confirmAction) => observer.next(confirmAction)),
    );
  }

  validateAllFormFields(formGroup: FormGroup) {
    Object.keys(formGroup.controls).forEach((field) => {
      const control = formGroup.get(field);
      if (control instanceof UntypedFormControl) {
        control.updateValueAndValidity({ onlySelf: true });
        control.markAsTouched();
      } else if (control instanceof FormGroup) {
        this.validateAllFormFields(control);
      }
    });
  }

  onSave() {
    const brandId = window.sessionStorage.getItem('brand_id');
    this.showSaveError = false;
    this.formSubmitted = true;

    const scheduleControl = this.form.get('schedule');

    this.form.markAllAsTouched();
    this.validateAllFormFields(scheduleControl as FormGroup);
    scheduleControl.updateValueAndValidity();

    if (this.form.invalid) {
      this.utilitiesService.findInvalidControls(this.form);
      this.showError = true;
      return;
    }

    if (this.form.valid) {
      const newJobs = this.form.get('newJobs').value;
      const schedule = this.form.get('schedule').value;
      const type = this.form.get('type').value;

      if (schedule) {
        const startDate =
          typeof schedule.startDate === 'object' && schedule.startDate
            ? this.dateService.format(schedule.startDate, 'YYYY-MM-DD')
            : schedule.startDate;
        const endDate =
          typeof schedule.endDate === 'object' && schedule.endDate
            ? this.dateService.format(schedule.endDate, 'YYYY-MM-DD')
            : schedule.endDate;

        const startDateInTz = startDate ? new Date(startDate) : new Date();
        const endDateInTz = endDate ? new Date(endDate) : new Date();

        schedule.startDate = this.dateService.format(
          this.dateService.convertToTimeZone(startDateInTz, schedule.timeZone),
          'YYYY-MM-DD',
        );
        schedule.endDate = this.dateService.format(
          this.dateService.convertToTimeZone(endDateInTz, schedule.timeZone),
          'YYYY-MM-DD',
        );

        if (schedule.frequency === 'MONTHLY') {
          delete schedule.frequencyModifier;
        }

        if (
          schedule.frequency === 'WEEKLY' ||
          schedule.frequency === 'DAILY' ||
          schedule.frequency === 'YEARLY'
        ) {
          delete schedule.dayOfMonth;
        }
      }
      const formatChannelIds = (items) => {
        return items.map((item) => `${this.programId}|${item}`);
      };

      const saveData: DistributionRule = {
        audience: 'SEGMENTS',
        channel: this.loyaltyRule ? this.form.get('loyaltyItemType').value : 'COUPON_WALLET',
        // needed because if field is disabled, form.value won't return that
        archived: this.form.get('archived').value,
        audienceIds: this.form.get('segments').value,
        channelIds: this.loyaltyRule
          ? formatChannelIds(this.form.get('items').value)
          : this.form.get('items').value,
        id: this.rule?.id || '',
        type,
        ...this.form.value,
        schedule,
      };

      if (type !== 'RECURRING') {
        delete saveData.schedule;
      }

      if (type === 'ONE_TIME') {
        delete saveData.audienceIds;
      }

      this.checkForArchiveWarning(saveData).subscribe((shouldSave) => {
        if (!shouldSave) {
          return;
        }

        this.isSaving = true;

        let obs = of(this.rule);

        if (!this.form.pristine) {
          obs = this.newDistributionRule
            ? this.create(brandId, saveData)
            : this.update(brandId, saveData);
        }

        obs.subscribe({
          next: (createdRule) => {
            const jobsObs = forkJoin(
              newJobs.map((job) =>
                job.id
                  ? this.distributionRulesService.editJob(brandId, job)
                  : this.distributionRulesService.addJob(brandId, createdRule.id, job),
              ),
            );

            jobsObs.subscribe({
              next: () => {
                this.form.markAsPristine();
                this.isSaving = false;
              },
              error: () => {
                this.isSaving = false;
                this.showSaveJobsError = true;
              },
              complete: () => {
                this.form.markAsPristine();
                this.isSaving = false;
                this.onBack();
              },
            });
          },
          error: this.handleSaveError.bind(this),
        });
      });
    }
  }

  create(brandId, saveData) {
    return this.distributionRulesService.create(brandId, saveData).pipe(first());
  }

  update(brandId, saveData) {
    return this.distributionRulesService.update(brandId, saveData.id, saveData).pipe(first());
  }

  onToggleJobForm() {
    const dialogRef = this.dialog.open(DistributionJobDialogComponent, {
      data: {
        segmentsList: this.segmentsList,
        rule: this.rule,
        job: this.editingJob,
        type: this.form.get('type').value,
      },
    });

    dialogRef.afterClosed().subscribe((newJobData) => {
      if (newJobData) {
        this.onSaveJob(newJobData);
      } else {
        this.addingJob = false;
        this.editingJob = undefined;
      }
    });
  }

  onSaveJob(newJobData) {
    const newJobsArray = (this.form.get('newJobs').value || []).filter(
      (job) => !job.id || job.id !== newJobData.id,
    );

    this.form.get('newJobs').setValue([...newJobsArray, newJobData]);

    if (this.editingJob) {
      this.jobsData.data = this.jobsData.data.map((job) => {
        if (job.id === newJobData.id) {
          Object.assign(job, newJobData);
        }

        return job;
      });
      this.editingJob = undefined;
    } else {
      this.jobsData.data = [...this.jobsData.data, newJobData];
      this.addingJob = false;
    }
  }

  deleteJobDialog(jobToRemove) {
    const startTime = this.dateService.format(jobToRemove.startTime, 'YYYY-MM-DD HH:mm:ss');
    const audienceCount = jobToRemove.audienceIds.length;
    const modalContent: ConfirmationDialogData = {
      title: this.translateService.instant('coupons.rules.grid.edit.jobs.deleteJobTitle'),
      message: this.translateService.instant('coupons.rules.grid.edit.jobs.deleteJobMsg', {
        startTime,
        audienceCount,
      }),
      cancelText: this.translateService.instant('form.button.cancel'),
      confirmText: this.translateService.instant('form.button.confirm'),
    };

    const dialogRef = this.dialog.open(ConfirmationComponent, {
      data: modalContent,
    });

    dialogRef.afterClosed().subscribe((confirmAction) => {
      if (confirmAction) {
        this.onRemoveJob(jobToRemove);
      }
    });
  }

  onRemoveJob(jobToRemove) {
    const brandId = window.sessionStorage.getItem('brand_id');

    if (jobToRemove.id) {
      this.cancellingJob = jobToRemove.id;
      this.distributionRulesService.cancelJob(brandId, jobToRemove.id).subscribe({
        next: () => {
          this.cancellingJob = undefined;

          if (this.rule?.id) {
            this.getJobList(this.rule.id);
          }
        },
        error: () => {
          this.showCancelJobError = true;
        },
        complete: () => {
          this.cancellingJob = undefined;
        },
      });
    } else {
      const newJobsArray = this.form.get('newJobs').value;

      this.form.get('newJobs').setValue(newJobsArray.filter((job) => job !== jobToRemove));
      this.jobsData.data = this.jobsData.data.filter((job) => job !== jobToRemove);
    }
  }

  showEditJobButton(job) {
    return job.status === 'SCHEDULED' && (!this.editingJob || this.editingJob.id !== job.id);
  }

  showRemoveJobButton(job) {
    const newLyAddedJob = !job.id;
    const scheduledOrRunningJob = job.status === 'SCHEDULED' || job.status === 'RUNNING';

    return newLyAddedJob || scheduledOrRunningJob;
  }

  onEditJob(jobToEdit) {
    this.editingJob = jobToEdit;
    this.onToggleJobForm();
  }

  onBack() {
    if (this.loyaltyRule) {
      this.router.navigate([`loyalty/program/${this.programId}/distribution-rules`]);
    } else {
      this.router.navigate(['../'], { relativeTo: this.route });
    }
  }

  handleSaveError() {
    this.showSaveError = true;
    this.isSaving = false;
  }
}
